// Minimal delimited-text parser for the Data Toolkit inputs. Handles both a
// pasted block and an uploaded CSV/TSV file. The column delimiter is auto-detected
// (tab vs comma) so data pasted from a spreadsheet — where cell values often
// contain their own commas, e.g. "Carles, MBA" — parses into the right columns.
// Supports quoted fields with embedded delimiters/quotes/newlines. No external dep.

// Pick the column delimiter from the first non-empty line: prefer TAB when the
// content is tab-separated (spreadsheet paste), otherwise fall back to comma.
export const detectDelimiter = text => {
  const norm = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const firstLine = norm.split("\n").find(l => l.trim() !== "") || "";
  const tabs = (firstLine.match(/\t/g) || []).length;
  return tabs > 0 ? "\t" : ",";
};

export const parseDelimited = (text, delimiter) => {
  const delim = delimiter || detectDelimiter(text);
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const s = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delim) {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // trailing field/row
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // drop fully-empty rows
  return rows.filter(r => r.some(cell => (cell || "").trim() !== ""));
};

// A cell that looks like a data value rather than a column label: an email, a
// URL, a bare domain, or a mostly-numeric/phone value. Used by the header
// auto-detector.
const looksLikeData = cell => {
  const v = (cell || "").trim();
  if (!v) return false;
  if (v.includes("@")) return true;
  if (/^https?:\/\//i.test(v)) return true;
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(v)) return true; // bare domain e.g. tesla.com
  if (/^\+?[\d\s().-]{5,}$/.test(v)) return true; // phone / long number
  return false;
};

// Known header labels we recognise for Find-email columns — a strong "this row
// is a header" signal.
const KNOWN_HEADER_TOKENS = new Set([
  "first name", "firstname", "fname", "given name",
  "last name", "lastname", "lname", "surname", "family name",
  "full name", "fullname", "contact name", "name",
  "company", "company name", "organization", "organisation", "employer", "account name",
  "company domain", "domain", "website", "url", "site",
  "linkedin", "linkedin url", "profile url",
  "email", "email address", "title", "job title"
]);

// Best-effort guess of whether the first parsed row is a header, so we never have
// to ask the user. Signals, in order: a data-like first cell (email/url/domain/
// number) means it is NOT a header; a recognised header label means it IS; a type
// mismatch (row 0 has no data-like cell but a following row does) means it IS.
// When there's no positive signal we default to NOT a header, so we never silently
// drop a real data row — the user can still flip the toggle in the mapping step.
export const detectHeader = matrix => {
  if (!matrix || matrix.length === 0) return false;
  const first = matrix[0].map(c => (c || "").trim());
  if (first.some(looksLikeData)) return false;
  if (first.some(c => KNOWN_HEADER_TOKENS.has(c.toLowerCase()))) return true;
  const sample = matrix.slice(1, 4);
  if (sample.some(r => r.some(looksLikeData))) return true;
  return false;
};

// Splits a parsed matrix into { headers, rows } given whether the first line is
// a header. When headerless, synthesizes Column 1..N labels.
export const toHeadersAndRows = (matrix, hasHeader) => {
  if (!matrix || matrix.length === 0) {
    return { headers: [], rows: [] };
  }
  const width = matrix.reduce((m, r) => Math.max(m, r.length), 0);
  const pad = r => {
    const out = r.slice(0, width);
    while (out.length < width) out.push("");
    return out;
  };
  if (hasHeader) {
    const headers = pad(matrix[0]).map((h, i) => (h && h.trim()) || `Column ${i + 1}`);
    const rows = matrix.slice(1).map(pad);
    return { headers, rows };
  }
  const headers = [];
  for (let i = 0; i < width; i++) headers.push(`Column ${i + 1}`);
  return { headers, rows: matrix.map(pad) };
};

// Up to `n` non-empty trimmed values from column `col`.
const columnSamples = (rows, col, n = 6) => {
  const out = [];
  for (let i = 0; i < rows.length && out.length < n; i++) {
    const v = ((rows[i] && rows[i][col]) || "").trim();
    if (v) out.push(v);
  }
  return out;
};

// Infers a field key from a column's actual values (used when the header gives no
// signal, e.g. a headerless paste). High-confidence types only — names/company
// names look alike as bare text, so we don't guess those from values.
const detectFieldFromValues = samples => {
  if (!samples.length) return null;
  const isLinkedin = v => /linkedin\.com\/in\//i.test(v);
  const isCompanyLinkedin = v => /linkedin\.com\/company\//i.test(v);
  const isEmail = v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  const isDomainish = v =>
    !isLinkedin(v) &&
    !isCompanyLinkedin(v) &&
    (/^https?:\/\//i.test(v) || /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(v));
  const majority = pred => samples.filter(pred).length >= Math.ceil(samples.length / 2);
  if (majority(isLinkedin)) return "linkedinUrl";
  if (majority(isCompanyLinkedin)) return "companyLinkedin";
  if (majority(isEmail)) return "email";
  if (majority(isDomainish)) return "companyDomain";
  return null;
};

// Guesses a field -> column-index mapping. First from header labels (exact match
// beats a loose "contains" match; `fields` order lets specific fields claim first),
// then from the column's values for anything still unmapped.
export const autoMap = (headers, rows, fields) => {
  const mapping = {};
  const usedCols = new Set();
  const usedFields = new Set();
  const lower = headers.map(h => (h || "").toLowerCase().trim());
  const fieldKeys = new Set(fields.map(f => f.key));

  const claim = predicate =>
    fields.forEach(field => {
      if (usedFields.has(field.key)) return;
      for (let i = 0; i < lower.length; i++) {
        if (usedCols.has(i) || !lower[i]) continue;
        if (field.match.some(m => predicate(lower[i], m))) {
          mapping[field.key] = i;
          usedCols.add(i);
          usedFields.add(field.key);
          break;
        }
      }
    });

  claim((header, token) => header === token); // pass 1: exact header
  claim((header, token) => header.includes(token)); // pass 2: header contains

  // Correction: a header like "LinkedIn Profile" often holds COMPANY page URLs
  // (a common CRM export mix-up). Trust the values over the header, and swap the
  // person/company LinkedIn fields when they disagree.
  const columnHasValues = col =>
    (rows || []).some(r => ((r[col] || "").trim() !== ""));
  const reassignByValues = (fromKey, toKey, wantKey) => {
    const col = mapping[fromKey];
    if (col == null || !fieldKeys.has(toKey)) return;
    if (detectFieldFromValues(columnSamples(rows || [], col)) !== wantKey) return;
    if (usedFields.has(toKey)) {
      const tCol = mapping[toKey];
      if (columnHasValues(tCol)) return; // real data already there, leave it
      delete mapping[toKey];
      usedFields.delete(toKey);
      usedCols.delete(tCol);
    }
    delete mapping[fromKey];
    usedFields.delete(fromKey);
    mapping[toKey] = col;
    usedFields.add(toKey);
  };
  reassignByValues("linkedinUrl", "companyLinkedin", "companyLinkedin");
  reassignByValues("companyLinkedin", "linkedinUrl", "linkedinUrl");

  // pass 3: infer from values for columns the header didn't resolve.
  for (let i = 0; i < headers.length; i++) {
    if (usedCols.has(i)) continue;
    const key = detectFieldFromValues(columnSamples(rows || [], i));
    if (key && fieldKeys.has(key) && !usedFields.has(key)) {
      mapping[key] = i;
      usedCols.add(i);
      usedFields.add(key);
    }
  }
  return mapping;
};
